import formatMessage from "sap/base/strings/formatMessage";
import ResourceModel from "sap/ui/model/resource/ResourceModel";

export default {
    formatTimeRange(start: string, end: string): string {
        if (!start || !end) {
            return "";
        }

        const startDate = new Date(start);
        const endDate = new Date(end);

        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
            return "";
        }

        const format = (d: Date) =>
            `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;

        return `${format(startDate)} - ${format(endDate)}`;
    },

    formatDate(sDate: string): string {
        if (!sDate) {
            return "";
        }

        if (sDate.includes("-") && !sDate.includes("T")) {
            const dateParts = sDate.split("-");
            if (dateParts.length === 3) {
                const [day, month, year] = dateParts;
                return `${day}.${month}.${year}`;
            }
        }

        const oDate = new Date(sDate);
        if (isNaN(oDate.getTime())) {
            return sDate;
        }

        const day = String(oDate.getDate()).padStart(2, "0");
        const month = String(oDate.getMonth() + 1).padStart(2, "0");
        const year = oDate.getFullYear();

        return `${day}.${month}.${year}`;
    },

    isCancellationEnabled(startTime: string, cancelled: boolean): boolean {
        try {
            if (cancelled) {
                return false;
            }
            if (!startTime) {
                return false;
            }
            const bookingStart = new Date(startTime);
            if (isNaN(bookingStart.getTime())) {
                return false;
            }
            // Allow cancellation for all bookings that are not cancelled
            return true;
        } catch {
            return false;
        }
    },

    formatLocalDateTime(date: Date): string {
        const pad = (n: number) => n.toString().padStart(2, "0");
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    },
    formatRoomNametoDesk(roomName: string): string {
        if (!roomName) {
            return "";
        }
        const parts = roomName.split("-");
        if (parts.length > 1) {
            return parts[1].trim();
        }
        return roomName.trim();
    },
    formatName(firstName: string, lastName: string): string {
        return `${firstName || ""} ${lastName || ""}`.trim();
    },

    getBookingStatusHighlight(startTime: string): string {
        if (!startTime) {
            return "None";
        }

        try {
            const bookingDate = new Date(startTime);
            const now = new Date();

            if (isNaN(bookingDate.getTime())) {
                return "None";
            }

            // If booking is in the past, apply past booking style
            if (bookingDate < now) {
                return "Warning"; // Orange/amber color for past bookings
            } else {
                // If booking is upcoming, apply upcoming booking style
                return "Success"; // Green color for upcoming bookings
            }
        } catch {
            return "None";
        }
    },

    getCanCancelVisibility(startTime: string, cancelled: boolean): boolean {
        try {
            if (cancelled) {
                return false;
            }
            if (!startTime) {
                return false;
            }
            const bookingStart = new Date(startTime);
            if (isNaN(bookingStart.getTime())) {
                return false;
            }
            const now = new Date();
            return now.getTime() < bookingStart.getTime();
        } catch {
            return false;
        }
    },

    formatDateForGrouping(sDate: string): string {
        if (!sDate) {
            return "";
        }

        const date = new Date(sDate);
        if (isNaN(date.getTime())) {
            return sDate;
        }

        const today = new Date();
        const diffDays = Math.floor(
            (date.setHours(0, 0, 0, 0) - today.setHours(0, 0, 0, 0)) / 86400000
        );

        // Get resource bundle from Core
        const oResourceModel = sap.ui.getCore().getModel("i18n") as ResourceModel;
        const oResourceBundle = oResourceModel?.getResourceBundle();

        if (diffDays === 0) {
            return (oResourceBundle as any)?.getText("dateGroupToday") || "Today";
        }
        if (diffDays === 1) {
            return (oResourceBundle as any)?.getText("dateGroupTomorrow") || "Tomorrow";
        }

        return (oResourceBundle as any)?.getText("dateGroupFutureBookings") || "Future Bookings";
    },

    formatMessage: formatMessage,

    formatSeatAvailabilityState(seatCount: string | number, maxSeats: string | number): string {
        const availableSeats = parseInt(String(seatCount), 10);
        const maximumSeats = parseInt(String(maxSeats), 10);

        if (isNaN(availableSeats) || isNaN(maximumSeats) || maximumSeats <= 0) {
            return "None";
        }

        if (availableSeats === 0) {
            return "None"; // Gray - no seats available
        }

        // Calculate availability ratio based on maximum seats
        const availabilityRatio = availableSeats / maximumSeats;

        // If all or more seats are available, show success (green)
        if (availabilityRatio >= 0.7) {
            return "Success"; // Green - plenty of seats (70%+ available)
        } else if (availabilityRatio >= 0.5) {
            return "Warning"; // Yellow/Orange - medium availability (50-69% available)
        } else {
            return "Error"; // Red - few seats left (1-49% available)
        }
    }
};
